/**
 * PulseRelease SDK
 * JavaScript SDK for displaying product announcements
 */

import { PulseRelease as PulseReleaseClass } from './core/PulseRelease';
import {
    Announcement,
    BoostType,
    CurrentUser,
    InitOptions,
    MetadataValue,
    UserInfo,
} from './types';

// Global instance
let instance: PulseReleaseClass | null = null;

/**
 * Detect API URL from script tag
 */
function detectApiUrl(): string | undefined {
    if (typeof document === 'undefined') {
        return undefined;
    }

    // Find the PulseRelease script tag
    const scripts = document.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; i++) {
        const script = scripts[i];
        const src = script.getAttribute('src');

        // Check if this is the PulseRelease SDK script
        if (src && (src.includes('pulse-release') || src.includes('pulserelease'))) {
            // Check for explicit data-api-url attribute
            const dataApiUrl = script.getAttribute('data-api-url');
            if (dataApiUrl) {
                return dataApiUrl;
            }

            // Extract base URL from script src. Resolve against the document
            // base so a relative src (e.g. "/js/pulserelease.js") doesn't throw.
            try {
                const url = new URL(src, document.baseURI);

                // The CDN hostname only serves the static bundle; the API lives
                // on the apex. Without this, loading from the CDN would send
                // API traffic through the CDN proxy, where per-IP rate limiting
                // sees the proxy's IP instead of the end user's.
                if (url.host === 'cdn.pulserelease.com') {
                    return 'https://pulserelease.com/api/sdk';
                }

                const baseUrl = `${url.protocol}//${url.host}`;
                return `${baseUrl}/api/sdk`;
            } catch {
                return undefined;
            }
        }
    }

    return undefined;
}

/**
 * PulseRelease SDK Public API
 */
export const PulseRelease = {
    /**
     * Initialize the SDK.
     *
     * Tolerates a missing or non-object argument at runtime: `init()` /
     * `init(null)` degrades exactly like any other invalid configuration
     * (a single warning, `isInitialized()` stays false) instead of
     * rejecting into the caller's page.
     */
    init: async (options: InitOptions): Promise<void> => {
        // Reading options.user / options.apiUrl below must never throw for
        // init() / init(null) — an async function's synchronous throw only
        // surfaces as an "Uncaught (in promise)" rejection for the common
        // fire-and-forget caller. Normalize a non-object argument to an
        // empty options bag so it flows into the same graceful
        // construction-failure path as a bad apiKey (Config's "apiKey is
        // required" warning below).
        if (options === null || typeof options !== 'object' || Array.isArray(options)) {
            options = {} as InitOptions;
        }

        if (instance) {
            // Fresh user data on a re-init (e.g. after the customer's login) is
            // treated as identify(): apply the new user context and re-fetch,
            // without a destroy() that would wipe the seen-set/anonymous id.
            //
            // On a GUEST instance this delegates to identify(), which is a
            // documented no-op in guest mode (emits the one-time
            // "unavailable in guest mode" warning). So calling init({ user })
            // against a guest session neither promotes it to a normal session
            // nor throws — it stays read-only. This is intentional: guest mode
            // is chosen at first init() and is not silently upgraded here.
            if (options.user) {
                await instance.identify(options.user);
                return;
            }

            if (instance.isReady()) {
                console.warn('PulseRelease: SDK already initialized');
                return;
            }

            // An instance exists but its init() never completed (backend was
            // unreachable, or the bounded retries were exhausted). Re-run init()
            // rather than refusing forever — otherwise a transient first-load
            // failure would leave the SDK dead for the whole session.
            await instance.init();
            return;
        }

        // Auto-detect API URL if not provided. Build a resolved copy instead of
        // mutating the caller's options object.
        let resolvedOptions = options;
        if (!options.apiUrl) {
            const detectedUrl = detectApiUrl();
            if (detectedUrl) {
                resolvedOptions = { ...options, apiUrl: detectedUrl };
            }
        }

        // Config validation (missing/malformed apiKey) throws synchronously
        // from this constructor. Uncaught, that would only surface as an
        // "Uncaught (in promise)" rejection of this async function for any
        // caller that doesn't await/catch init() — silent by default, the
        // exact failure mode this fix targets. Warn clearly instead, and
        // leave `instance` unset so the SDK degrades like any other failed
        // init rather than throwing into the customer's page.
        try {
            instance = new PulseReleaseClass(resolvedOptions);
        } catch (error) {
            console.warn(
                error instanceof Error ? error.message : 'PulseRelease: invalid configuration.'
            );
            return;
        }

        await instance.init();
    },

    /**
     * Update the current user context after the customer's own auth state
     * changes (typically a login). Re-establishes the session for the identified
     * user and refreshes announcements, WITHOUT clearing the persisted seen-set
     * or anonymous id (unlike destroy() + init()). Warns and no-ops if init() was
     * never called.
     *
     * When your application requires identity verification, include a fresh
     * `user.hash` for the user being identified (computed by your backend —
     * see `UserInfo.hash`); without it the identify call is rejected
     * server-side.
     *
     * A non-object argument is ignored with a warning rather than thrown.
     */
    identify: async (user: UserInfo): Promise<void> => {
        if (!instance) {
            console.warn('PulseRelease: call init() before identify()');
            return;
        }

        return instance.identify(user);
    },

    /**
     * Return the current visitor's identifiers — `{ id, email, anonymousId,
     * isAnonymous }` — synchronously, with no network call. Use them to honor an
     * opt-out / GDPR "right to be forgotten" request: send the relevant one to
     * your server, which calls `DELETE /api/v1/users`. Remove an IDENTIFIED
     * visitor by `id`/`email`; `anonymousId` only identifies a still-anonymous
     * visitor. Returns null if init() has not completed yet.
     */
    getUser: (): CurrentUser | null => {
        if (!instance || !instance.isReady()) {
            return null;
        }

        return instance.getUser();
    },

    /**
     * Get unread count. Returns 0 if init never completed (e.g. backend was
     * unreachable) so customer code can render UI without special-casing.
     */
    getUnreadCount: async (): Promise<number> => {
        if (!instance || !instance.isReady()) {
            return 0;
        }

        return instance.getUnreadCount();
    },

    /**
     * Open the announcements list modal. Serviced as soon as init() has STARTED:
     * when the SDK is ready it opens and populates immediately; when init() is
     * still in flight the modal comes up instantly in a loading state and
     * populates the moment init settles. No-ops only when init() was never
     * called, or it already failed and gave up — there is nothing to render and
     * we'd rather degrade silently than throw into customer code.
     */
    openAnnouncements: async (): Promise<void> => {
        if (!instance) {
            return;
        }

        if (!instance.isReady() && !instance.isInitializing()) {
            return;
        }

        return instance.openAnnouncements();
    },

    /**
     * Fetch the latest published announcements as raw JSON — nothing is rendered —
     * so you can build your own "what's new" / changelog UI (for example a panel
     * on your login or settings page). Newest first, including active and past
     * announcements, in the SDK's active language — the `language` you pass to
     * `init()`, or the visitor's auto-detected browser language when you don't set
     * one. Returns an empty array if init() has not completed yet.
     *
     * @param limit maximum number to return (default 3, max 20)
     */
    getAnnouncements: async (limit: number = 3): Promise<Announcement[]> => {
        if (!instance || !instance.isReady()) {
            return [];
        }

        try {
            return await instance.getAnnouncements(limit);
        } catch {
            return [];
        }
    },

    /**
     * Render a sample of a boost type on demand, using built-in sample data —
     * a handy way for developers to see exactly how each announcement style
     * (popup, top bar, persistent top bar, toast) looks without creating real
     * announcements. Requires init() to have been called first.
     *
     * @param boostType one of 'full_page_popup' | 'top_bar' |
     *   'persistent_top_bar' | 'toast_notification' | 'list'
     */
    preview: (boostType: BoostType | 'list'): void => {
        if (!instance) {
            console.warn('PulseRelease: call init() before preview()');
            return;
        }

        instance.preview(boostType);
    },

    /**
     * Close any open announcement
     */
    close: (): void => {
        if (!instance) {
            return;
        }

        instance.close();
    },

    /**
     * Attach a piece of custom metadata to the current visitor — e.g. plan
     * tier, account age, or any other attribute you want to target
     * announcements by. Values are strings or numbers; booleans coerce to
     * "true"/"false", and any other type (or a key/value past the size
     * limits) is silently dropped. Applies locally right away — reflected in
     * the next `getUser()` call — and syncs to the server in a short
     * debounced batch. Warns and no-ops if init() was never called.
     */
    setMetadata: (name: string, value: MetadataValue | boolean): void => {
        if (!instance) {
            console.warn('PulseRelease: call init() before setMetadata()');
            return;
        }

        instance.setMetadata(name, value);
    },

    /**
     * Remove a single custom metadata key from the current visitor, locally
     * and (via the debounced flush) on the server. Warns and no-ops if init()
     * was never called.
     */
    removeMetadata: (name: string): void => {
        if (!instance) {
            console.warn('PulseRelease: call init() before removeMetadata()');
            return;
        }

        instance.removeMetadata(name);
    },

    /**
     * Remove all custom metadata for the current visitor, locally and (via
     * the debounced flush) on the server. Warns and no-ops if init() was
     * never called.
     */
    clearMetadata: (): void => {
        if (!instance) {
            console.warn('PulseRelease: call init() before clearMetadata()');
            return;
        }

        instance.clearMetadata();
    },

    /**
     * Mark announcement as read
     */
    markAsRead: async (announcementId: string): Promise<void> => {
        if (!instance || !instance.isReady()) {
            return;
        }

        return instance.markAsRead(announcementId);
    },

    /**
     * Mark all announcements as read
     */
    markAllAsRead: async (): Promise<void> => {
        if (!instance || !instance.isReady()) {
            return;
        }

        return instance.markAllAsRead();
    },

    /**
     * Destroy SDK instance
     */
    destroy: (): void => {
        if (!instance) {
            return;
        }

        instance.destroy();
        instance = null;
    },

    /**
     * Check whether the SDK is ready to handle method calls.
     *
     * Returns false if init() has not been called, or if it was called but
     * failed (e.g. the API was unreachable and we swallowed the error so the
     * customer page wouldn't break). This reflects "ready to use" — not "an
     * instance was constructed".
     */
    isInitialized: (): boolean => {
        return instance !== null && instance.isReady();
    },

    /**
     * Whether the SDK is running in guest mode — a read-only, session-less
     * integration started with `init({ guest: true })`. In guest mode the SDK
     * registers no visitor, keeps no client-side state, never auto-renders a
     * boost, and serves only the on-demand announcements list; every mutator
     * (identify, metadata setters, mark-as-read) no-ops. Returns false when the
     * SDK was initialized normally or was never constructed.
     */
    isGuest: (): boolean => {
        return instance !== null && instance.isGuest();
    },
};

// Export types for TypeScript users
export * from './types';

// Make available globally for non-module scripts
if (typeof window !== 'undefined') {
    (window as any).PulseRelease = PulseRelease;
}

// Default export
export default PulseRelease;
